import { useState, useEffect, useCallback } from "react";
import MyOrders from "@/components/dashboard/MyOrders";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Package, Palette, Clock, ShoppingBag,
  MessageCircle, LogOut, User, ChevronRight, Bell, BellOff,
} from "lucide-react";
import { getPushStatus, subscribeUserPush, unsubscribeUserPush } from "@/lib/push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard({ user, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [favColours, setFavColours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushStatus, setPushStatus] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus === "subscribed") {
        await unsubscribeUserPush();
      } else {
        await subscribeUserPush();
      }
      setPushStatus(await getPushStatus());
    } catch (err) {
      console.warn("Push toggle failed:", err.message);
    } finally {
      setPushBusy(false);
    }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/user/dashboard`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
        setFavColours(data.favourite_colours || []);
      }
    } catch (err) {
      console.debug("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    getPushStatus().then(setPushStatus).catch(() => {});
  }, [fetchDashboard]);

  const formatDate = (ts) => {
    return new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--mp-paper)" }} data-testid="client-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[var(--mp-line)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="dashboard-back-btn">
              <Button variant="ghost" className="p-2 rounded-sm">
                <ArrowLeft className="w-5 h-5" style={{ color: "var(--mp-text-strong)" }} />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
                My Account
              </h1>
              <p className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                Welcome, {user?.name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pushStatus !== "unsupported" && pushStatus !== "denied" && (
              <Button
                variant="outline"
                onClick={togglePush}
                disabled={pushBusy}
                className="text-xs font-medium px-3 py-2 rounded-sm hidden sm:flex items-center gap-1.5"
                style={{
                  borderColor: pushStatus === "subscribed" ? "#101010" : "#E4DDCE",
                  color: pushStatus === "subscribed" ? "#101010" : "#5C5C5C",
                  fontFamily: "'Manrope', sans-serif",
                }}
                data-testid="user-push-toggle"
                title="Get push notifications when your order status updates"
              >
                {pushStatus === "subscribed" ? (
                  <><Bell className="w-3.5 h-3.5" /> Updates ON</>
                ) : (
                  <><BellOff className="w-3.5 h-3.5" /> Order Updates</>
                )}
              </Button>
            )}
            <Link to="/colours">
              <Button className="text-white text-xs font-medium px-4 py-2 rounded-sm" style={{ backgroundColor: "var(--mp-solid)", fontFamily: "'Manrope', sans-serif" }} data-testid="dashboard-order-btn">
                <ShoppingBag className="w-4 h-4 mr-1" /> Order Now
              </Button>
            </Link>
            <Button variant="ghost" onClick={onLogout} className="p-2 rounded-sm" data-testid="dashboard-logout-btn">
              <LogOut className="w-5 h-5" style={{ color: "var(--mp-copper)" }} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* User Info Card */}
        <div className="pastel-green border border-[var(--mp-line)] rounded-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="user-info-card">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--mp-solid)" }}>
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-medium" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>{user?.name}</h2>
              <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>{user?.phone}</p>
            </div>
          </div>
          <div className="flex gap-6">
            <div className="text-center">
              <span className="text-2xl font-medium block" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}>{orders.length}</span>
              <span className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>Orders</span>
            </div>
            <div className="text-center">
              <span className="text-2xl font-medium block" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text-strong)" }}>{favColours.length}</span>
              <span className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>Favourite Colours</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Orders */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-medium mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
              Your Orders
            </h3>
            <MyOrders />
          </div>

          {/* Favourite Colours */}
          <div>
            <h3 className="text-xl font-medium mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}>
              Your Frequent Colours
            </h3>
            {favColours.length === 0 ? (
              <div className="pastel-lavender border border-[var(--mp-line)] rounded-sm p-6 text-center">
                <Palette className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--mp-copper-deep)" }} />
                <p className="text-sm" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                  Your frequently ordered colours will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {favColours.map((col) => (
                  <div key={`${col.number}-${col.family}`} className="bg-white border border-[var(--mp-line)] rounded-sm p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>
                        #{col.number}
                      </span>
                      <span className="text-xs" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-muted)" }}>
                        {col.name} - {col.family}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Quick Actions */}
            <div className="mt-6 space-y-3">
              <h4 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>Quick Actions</h4>
              <a href="https://wa.me/917624853840" target="_blank" rel="noopener noreferrer" className="block">
                <div className="bg-white border border-[var(--mp-line)] rounded-sm p-4 flex items-center gap-3 product-card">
                  <MessageCircle className="w-5 h-5" style={{ color: "#25D366" }} />
                  <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>Chat on WhatsApp</span>
                </div>
              </a>
              <a href="https://instagram.com/theliningstoreindia" target="_blank" rel="noopener noreferrer" className="block">
                <div className="bg-white border border-[var(--mp-line)] rounded-sm p-4 flex items-center gap-3 product-card">
                  <Palette className="w-5 h-5" style={{ color: "#E1306C" }} />
                  <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>Follow on Instagram</span>
                </div>
              </a>
              <Link to="/exchange-policy" className="block">
                <div className="bg-white border border-[var(--mp-line)] rounded-sm p-4 flex items-center gap-3 product-card">
                  <Clock className="w-5 h-5" style={{ color: "var(--mp-copper)" }} />
                  <span className="text-sm font-medium" style={{ fontFamily: "'Manrope', sans-serif", color: "var(--mp-text)" }}>Exchange Policy</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
